import React from 'react';
import {Atom, withSystem} from '@packages/systems/wfdl';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  useFrameOrientation,
  usePreviewMode,
  useMediaQuery,
} from '@packages/systems/render';
import {getBreakpointContainerName} from './utils/containerUtils';
import {
  getBreakpointWidth,
  PrimitiveFrameLayout,
} from '@packages/systems/open-canvas/ui';
import {getBreakpointID} from '@packages/systems/style/types';
import {FrameMeasurer} from './FrameMeasurements';
import {getChildren} from '@packages/utilities/data-utils';

const FrameLayoutRender = Atom.createRender(
  ({data, attributes, classes, generateAttributes, renderChild}) => {
    const orientation = useFrameOrientation();

    const mediaQuery = useMediaQuery() ?? 'main'; // current designer breakpoint
    const breakpointContainerName = getBreakpointContainerName(mediaQuery);
    const breakpointId = getBreakpointID(mediaQuery) ?? 'main';
    const breakpointWidth = getBreakpointWidth(breakpointId);

    const {style: generatedStyle} = generateAttributes(attributes, classes);
    const isPreviewMode = usePreviewMode();

    const layoutProps = {
      width: breakpointWidth,
      orientation: orientation?.mode ?? ('vertical' as const),
      gap: orientation?.gap ?? 0,
      crossAxisTrackCount: orientation?.crossAxisTrackCount,
      breakpointContainerName,
      filter: generatedStyle?.filter,
    };

    if (isPreviewMode) {
      return (
        <PrimitiveFrameLayout {...layoutProps}>
          {getChildren(data).map((child) => renderChild(child))}
        </PrimitiveFrameLayout>
      );
    }

    return (
      <FrameMeasurer>
        {(ref) => (
          <PrimitiveFrameLayout {...layoutProps} ref={ref}>
            {getChildren(data).map((child) => renderChild(child))}
          </PrimitiveFrameLayout>
        )}
      </FrameMeasurer>
    );
  }
);

const config = createIntegratedAtom({
  showEmpty: false,
  displayName: 'Frame Layout',
  render: FrameLayoutRender,
  fields: {
    children: withSystem({private: true}, TListOfElement),
  },
  constraints: {
    parent: [{is: 'Body', rule: 'ExactlyOne'}],
    children: [
      {
        is: 'Frame',
        rule: 'RequireOnly',
        messages: {
          move: 'Frame Layout can only contain Frame elements.',
        },
      },
    ],
    pinToParent: true,
  },
});

export default config;
